import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { AuthContextType } from '../types/auth';
import { User } from '../types/user';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import { useLanguage } from './LanguageContext';
import { doc, updateDoc } from 'firebase/firestore';
import { activityLogService } from '../services/activityLogService';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<(FirebaseUser & User) | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  // Update the lastLoginAt field for the user
  const updateLastLogin = async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastLoginAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating last login time:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userData = await userService.createUserIfNotExists(firebaseUser);
          
          // Update the lastLoginAt timestamp
          await updateLastLogin(firebaseUser.uid);
          
          // Include the lastLoginAt in the user data
          const updatedUserData = {
            ...userData,
            lastLoginAt: new Date()
          };
          
          setUser({ ...firebaseUser, ...updatedUserData });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error setting user:', error);
        toast.error(t('common.messages.authContext.error'));
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      
      // Update last login time when user signs in
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      toast.success(t('common.messages.authContext.signInSuccess'));
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      
      // MFA required - throw error to be handled by Login component
      if (error.code === 'auth/multi-factor-auth-required') {
        throw error; // Re-throw to be caught by Login component
      }
      
      toast.error(t('common.messages.authContext.signInError'));
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time when user signs in
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      if (result.user) {
        activityLogService.log({
          userId: result.user.uid,
          userEmail: result.user.email || '',
          userName: result.user.displayName || '',
          action: 'login',
          resource: 'auth',
          details: 'Sikeres bejelentkezés'
        });
      }

      toast.success(t('common.messages.authContext.signInSuccess'));
      return { success: true };
    } catch (error: any) {
      console.error('Error signing in with email:', error);
      
      // MFA required - throw error to be handled by Login component
      if (error.code === 'auth/multi-factor-auth-required') {
        throw error; // Re-throw to be caught by Login component
      }
      
      // Too many requests - throw to be handled by Login component with our lockout message
      if (error.code === 'auth/too-many-requests') {
        throw error;
      }
      
      let errorMessage = t('common.messages.authContext.signInError');
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Nincs ilyen felhasználó';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Helytelen jelszó';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Érvénytelen email cím';
      }
      
      activityLogService.log({
        userId: '',
        userEmail: email,
        userName: '',
        action: 'login_failed',
        resource: 'auth',
        details: `${error.code}: ${errorMessage}`
      }).catch(() => {});

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore
      if (result.user) {
        await userService.createUserIfNotExists(result.user);
        await updateLastLogin(result.user.uid);
      }
      
      toast.success('Sikeres regisztráció!');
      return { success: true };
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      let errorMessage = 'Hiba történt a regisztráció során';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Ez az email cím már használatban van';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Érvénytelen email cím';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'A jelszó túl gyenge (minimum 6 karakter)';
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      if (user) {
        await activityLogService.log({
          userId: user.uid,
          userEmail: user.email || '',
          userName: user.displayName || '',
          action: 'logout',
          resource: 'auth',
          details: 'Kijelentkezés'
        });
      }
      await firebaseSignOut(auth);
      toast.success(t('common.messages.authContext.signOutSuccess'));
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error(t('common.messages.authContext.signOutError'));
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Jelszó visszaállító email elküldve!');
      return { success: true };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      let errorMessage = 'Hiba történt a jelszó visszaállítás során';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Nincs ilyen felhasználó';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Érvénytelen email cím';
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 